import type { DashboardData } from '../MyAccount.types';

interface DashboardPageProps {
  data: DashboardData;
}

const ChevronIcon = () => (
  <span className="t-chevron">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
  </span>
);

export const DashboardPage = ({ data }: DashboardPageProps) => {
  const hasLinkedCard = data.hasLinkedLoyaltyCard;
  const formattedValue = data.loyaltyValue !== null ? `€${data.loyaltyValue.toFixed(2)}` : '€0.00';
  const formattedPoints = data.loyaltyPoints !== null ? data.loyaltyPoints.toLocaleString() : '0';
  const paymentOptionsUrl = data.tiles.find(t => t.icon === 'payment')?.url ?? '#';

  return (
  <>
    {/* Top row: loyalty + profile */}
    <div className="dash-top">
      <div className={`dash-loyalty${hasLinkedCard ? '' : ' is-empty'}`}>
        {/* Enrolled state */}
        <div className="lc-top">
          <p className="lc-label">My loyalty points</p>
        </div>
        <div className="lc-balance">
          <p className="lc-value">{formattedValue}</p>
          <p className="lc-points"><strong>{formattedPoints}</strong> points</p>
          <p className="lc-redeem">Redeemable at checkout, online or in store.</p>
        </div>
        {/* Not-enrolled state */}
        <div className="lc-empty">
          <span className="lc-empty-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"/><path d="M13 5v14"/></svg>
          </span>
          <h3>Join Eason Rewards</h3>
          <p>You're not a member yet. Link your loyalty card to earn points on every order and redeem them at checkout.</p>
          <a href={paymentOptionsUrl} className="lc-link">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
            Link your card
          </a>
        </div>
      </div>
      <div className="dash-profile">
        <div className="pf-head">
          <div>
            <p className="pf-name">{data.firstName} {data.lastName}</p>
            <p className="pf-email">{data.email}</p>
          </div>
        </div>
        <a href={data.signOutUrl} className="pf-signout">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
          Sign out
        </a>
      </div>
    </div>

    {/* Account tiles */}
    <h3 className="dash-section-label">Manage your account</h3>
    <div className="dash-tiles is-list">
      {data.tiles.map(tile => (
        <a key={tile.url} href={tile.url} className="dash-tile">
          <span className="t-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              {tile.icon === 'orderhistory' && <><path d="M21 8 12 3 3 8v8l9 5 4-2.2"/><path d="m3 8 9 5 9-5"/><path d="M12 13v4"/><circle cx="18.5" cy="16.5" r="4.2"/><path d="M18.5 14.6v1.9l1.3.9"/></>}
              {tile.icon === 'security' && <><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/><circle cx="12" cy="16" r="1.5"/></>}
              {tile.icon === 'payment' && <><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/><path d="M6 15h4"/></>}
              {tile.icon === 'giftcard' && <><path d="M20 12v8H4v-8"/><path d="M2 7h20v5H2z"/><path d="M12 22V7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></>}
              {tile.icon === 'address' && <><path d="M3 10.5 12 3l9 7.5"/><path d="M5 9.5V21h14V9.5"/><path d="M9 21v-6h6v6"/></>}
            </svg>
          </span>
          <span className="t-body">
            <span className="t-title">{tile.title}</span>
            <span className="t-desc">{tile.description}</span>
          </span>
          <ChevronIcon />
        </a>
      ))}
    </div>
  </>
  );
};
